import {Observable} from "rxjs";
import {InvalidEchoError} from "../errors/invalid-echo-error";

export interface LogErrorsDecoratorOptions<T> {
  log?: "error" | "warn" | "log" | "info" | "trace"; // which console statement to use
  onError?: undefined | "throw" | ((self: any, error: Error) => (undefined | never | T));
}

function isPromise(obj): boolean {
  return obj && ((obj instanceof Promise) || (typeof obj.then === "function"));
}

function isObservable(obj): boolean {
  return obj && ((obj instanceof Observable) || (typeof obj.subscribe === "function"));
}

function log(level, message) {
  if (console) {
    if (typeof console[level] !== "function") level = "error";
    console[level](message);
  }
}

/**
 * Handle any error output from the wrapped function.
 * This acts like the handleErrors decorator but just logs (useful for services, please don't use in a component).
 * Guarantees errors will be handled if thrown synchronously or via promise/observable (including async/await).
 * Does not affect return type (e.g. will not coerce synchronous functions or Observables into Promises)
 *
 * @param {boolean} [options.log] how to log the error
 *  default true
 * @param {(self: any, error: Error) => (undefined | T)} [options.onError] what to do on error
 *  Allowed values include undefined, "throw", or a Function that receives self (this) and the error
 *  default undefined
 * @returns {(target, propertyKey: string, descriptor: PropertyDescriptor) => PropertyDescriptor}
 */
export function logErrors<T>(options: LogErrorsDecoratorOptions<T> = {}) {
  if (!Object.prototype.hasOwnProperty.call(options, "log")) options.log = "error";
  if (!Object.prototype.hasOwnProperty.call(options, "onError")) options.onError = undefined;

  return function logErrorsDecorator(target, propertyKey: string, descriptor: PropertyDescriptor) {
    if (descriptor === undefined) {
      descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    }

    const original = descriptor.value; // warning: this === undefined

    descriptor.value = function (...args) { // eslint-disable-line func-names
      const self = this;
      const method = original.bind(self);

      let result;
      let errorHandlerRun = false;

      const errorHandler = (error) => {
        if (errorHandlerRun) return undefined;
        errorHandlerRun = true;

        if (InvalidEchoError.test(error)) return undefined;

        log(log, error);

        if (options.onError === "throw") throw error;

        if (typeof options.onError === "function") {
          return (options.onError as Function).bind(self)(self, error);
        }

        return undefined;
      };

      try {
        result = method(...args);
      } catch (error) {
        return errorHandler(error);
      }

      if (isPromise(result)) {
        return Promise.resolve(result as Promise<T>)
          .catch(errorHandler);
      }

      if (isObservable(result)) {
        return (result as Observable<T>)
          .catch(errorHandler);
      }

      return result;
    };

    return descriptor;
  };
}
